import { useState, useEffect } from 'react';
import { cachedFetchJSON, GEO_TTL } from '../utils/fetchCache';

// EPA EJSCREEN block-group-level environmental justice data
// gispub.epa.gov is the current public-facing ArcGIS endpoint for EJSCREEN.
// If this fails, download the national CSV from https://www.epa.gov/ejscreen/download-ejscreen-data
// and host a state-filtered file at /public/data/ejscreen_{stateFips}.json.
const EJSCREEN_URL =
  'https://gispub.epa.gov/arcgis/rest/services/OEI/EJScreen_v2_2024/MapServer/0/query';

// Block group GEOIDs are 12 digits. Population field used for tract-level aggregation.
const POP_FIELD = 'ACSTOTPOP';
const EJSCREEN_FIELDS = ['CANCER', 'RESP', 'PM25', 'OZONE', 'DSLPM', 'PTRAF', 'PRE1960PCT', 'PNPL'];

async function fetchBlockGroups(stateFips, countyFips) {
  const fips5 = `${stateFips}${countyFips}`;
  const where = `ID LIKE '${fips5}%'`;
  const outFields = ['ID', POP_FIELD, ...EJSCREEN_FIELDS].join(',');
  const url =
    `${EJSCREEN_URL}` +
    `?where=${encodeURIComponent(where)}` +
    `&outFields=${outFields}` +
    `&f=json&resultRecordCount=2000`;
  const data = await cachedFetchJSON(url, GEO_TTL);
  return (data?.features ?? []).map(f => {
    const a = f.attributes ?? {};
    return {
      id: String(a.ID ?? ''),
      pop: parseFloat(a[POP_FIELD]) || 0,
      ...Object.fromEntries(EJSCREEN_FIELDS.map(k => [k, a[k]]))
    };
  });
}

// Population-weighted average of an EJSCREEN field, aggregated to census tract level.
// Block group ID is 12 digits; tract is the first 11 digits.
function aggregateToTracts(blockGroups, field) {
  const tractGroups = new Map();
  for (const bg of blockGroups) {
    if (bg.id.length < 12) continue;
    const tractId = bg.id.substring(0, 11);
    if (!tractGroups.has(tractId)) tractGroups.set(tractId, []);
    tractGroups.get(tractId).push(bg);
  }
  const result = [];
  for (const [tractId, bgs] of tractGroups.entries()) {
    const valid = bgs
      .map(bg => ({ v: parseFloat(bg[field]), pop: bg.pop }))
      .filter(x => !isNaN(x.v));
    if (!valid.length) continue;
    const totalPop = valid.reduce((s, x) => s + x.pop, 0);
    const value = totalPop > 0
      ? valid.reduce((s, x) => s + x.v * x.pop, 0) / totalPop
      : valid.reduce((s, x) => s + x.v, 0) / valid.length;
    result.push({
      geoid: tractId,
      name: `Tract ${tractId.slice(-6)}`,
      value: Math.round(value * 100) / 100
    });
  }
  return result;
}

/**
 * Hook: fetches EPA EJSCREEN environmental indicators at block group level,
 * then aggregates to census tract via population-weighted average.
 *
 * EJSCREEN is single-vintage (no historical trend). Trend array has one point.
 * stateValue is not computed (would require full state fetch).
 */
export function useEJScreenData(config, overrideIndicators, geographyOverride) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const indicators = (overrideIndicators ?? config.indicators).filter(i => i.source === 'ejscreen');
    if (indicators.length === 0) { setData(new Map()); setLoading(false); return; }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { state: stateFips, county: countyFips } = config.org.geography;

        // One fetch covers all EJSCREEN indicators for the county
        const blockGroups = await fetchBlockGroups(stateFips, countyFips);

        const results = new Map();
        for (const indicator of indicators) {
          const field = indicator.ejscreenField;
          const years = indicator.years ?? [2024];
          const latestYear = years[years.length - 1];

          const geography = aggregateToTracts(blockGroups, field);

          // County value: population-weighted average across all block groups
          const validBgs = blockGroups
            .map(bg => ({ v: parseFloat(bg[field]), pop: bg.pop }))
            .filter(x => !isNaN(x.v));
          const totalPop = validBgs.reduce((s, x) => s + x.pop, 0);
          const rawCounty = validBgs.length
            ? (totalPop > 0
                ? validBgs.reduce((s, x) => s + x.v * x.pop, 0) / totalPop
                : validBgs.reduce((s, x) => s + x.v, 0) / validBgs.length)
            : null;
          const countyValue = rawCounty !== null ? Math.round(rawCounty * 100) / 100 : null;

          // Single-year data — trend has one point; no state comparison available
          const trend = countyValue !== null ? [{ year: latestYear, value: countyValue }] : [];
          const geographyByYear = new Map([[latestYear, geography]]);

          results.set(indicator.id, {
            indicatorId: indicator.id,
            label: indicator.label,
            unit: indicator.unit,
            geography,
            geographyByYear,
            stateGeography: [],
            trend,
            stateTrend: [],
            countyValue,
            stateValue: null
          });
        }

        if (!cancelled) { setData(results); setLoading(false); }
      } catch (err) {
        if (!cancelled) {
          setError(`EJSCREEN data failed to load: ${err.message}`);
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [config, overrideIndicators, geographyOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error };
}
